import React, { useEffect, useState, useCallback, useRef } from "react";
import Chart from "react-apexcharts";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import { dimensionFuture } from "../services/api-services";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ZoomModal from "./heatmap/zoomModal";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const DimenstionFutureChart = ({
  selectedCompanyId,
  selectedDimensionId,
  selectedCampaignId,
  type,
  graphName,
}) => {
  const [responseBody, setResponseBody] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const modalRef = useRef(null);

  const [zoomData, setZoomData] = useState({
    modalId: "",
    chartOptions: {},
    seriesData: [],
    title: "",
  });

  //Fetch API data
  const getDimenstionFutureData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const response = await dimensionFuture(
        selectedCompanyId,
        selectedCampaignId,
        type,
        graphName
      );

      if (response?.data?.responseCode === 200) {
        const body = response?.data?.responseBody || [];

        const filteredData = selectedDimensionId
          ? body.filter(
            (item) =>
              String(item.dimensionId || item.id).trim() ===
              String(selectedDimensionId).trim()
          )
          : body;

        setResponseBody(filteredData);
        setRemarkText(response?.data?.remark || "");
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);
      } else {
        setResponseBody([]);
        toastEmitter("error", response?.data?.message || "No data found");
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedCampaignId, type, graphName, selectedDimensionId]);

  useEffect(() => {
    getDimenstionFutureData();
  }, [getDimenstionFutureData]);

  // Chart generator
  const generateChartData = (subdimension) => {
    const list = subdimension?.questionResponseList || [];
    if (!list.length) return { options: {}, series: [] };

    const labels = list.map((i) => i.criteriaShortLabel || "N/A");
    const colors = ["#D42327", "#fd8000", "#ffbf01", "#dbc51a", "#92d14e"];

    const series = [
      "Initiating",
      "Emerging",
      "Performing",
      "Advancing",
      "Leading",
    ].map((name) => ({
      name,
      data: list.map((i) => i?.[`${name.toLowerCase()}Value`] ?? 0),
    }));

    return {
      options: {
        chart: { type: "bar", stacked: true, stackType: "100%" },
        plotOptions: { bar: { horizontal: true, barHeight: "35%" } },
        dataLabels: { enabled: true, style: { fontSize: "9px", colors: ["#000"] } },
        xaxis: { categories: labels },
        fill: { colors, opacity: 1 },
        legend: { position: "top", markers: { fillColors: colors } },
      },
      series,
    };
  };

  // Excel export
  const exportAsExcel = (data, title) => {
    if (!data?.length) return;
    const headers = [
      "Subdimension",
      "Initiating",
      "Emerging",
      "Performing",
      "Advancing",
      "Leading",
    ];
    const rows = [];
    data.forEach((dim) =>
      dim.dimensionResponseList?.forEach((sub) => {
        const vals = sub.questionResponseList.reduce(
          (acc, q) => {
            acc.initiating += q.initiatingValue || 0;
            acc.emerging += q.emergingValue || 0;
            acc.performing += q.performingValue || 0;
            acc.advancing += q.advancingValue || 0;
            acc.leading += q.leadingValue || 0;
            return acc;
          },
          { initiating: 0, emerging: 0, performing: 0, advancing: 0, leading: 0 }
        );
        rows.push([
          sub.subDimensionName,
          vals.initiating,
          vals.emerging,
          vals.performing,
          vals.advancing,
          vals.leading,
        ]);
      })
    );

    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `${title || "DimensionFuture"}.xlsx`);
  };

  // PDF export
  const exportAsPDF = async (chartId, title) => {
    const chartEl = document.querySelector(`#${chartId}`);
    if (!chartEl) return;
    const canvas = await html2canvas(chartEl);
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height * w) / canvas.width;
    pdf.text(title || "Chart", 10, 10);
    pdf.addImage(img, "PNG", 10, 20, w - 20, h);
    pdf.save(`${title || "chart"}.pdf`);
  };

  //Image export
  const exportAsImage = async (chartId, title) => {
    const chartEl = document.querySelector(`#${chartId}`);
    const canvas = await html2canvas(chartEl);
    const img = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = img;
    link.download = `${title || "chart"}.png`;
    link.click();
  };

  // Zoom handler
  const openZoomModal = (modalId, title, options, seriesData) => {
    setZoomData({ modalId, chartOptions: options, seriesData, title });
    const modalEl = document.getElementById(modalId);
    const bs = window.bootstrap;
    if (modalEl && bs) {
      const modal = bs.Modal.getOrCreateInstance(modalEl);
      modal.show();
    }
  };

  return (
    <>
      <div className="card p-2">
        <h5>Dimension - Future</h5>

        <div className="dimension-future-wrapper">
          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              {responseBody.map((dimension, di) => (
                <div key={di} className="mb-4">
                  {dimension.dimensionResponseList.map((sub, si) => {
                    const { options, series } = generateChartData(sub);
                    const chartId = `chart-${di}-${si}`;
                    const modalId = `zoomModal-${di}-${si}`;

                    return (
                      <div
                        key={`${di}-${si}`}
                        id={`chart-box-${sub.subDimensionName}`}
                        className="card h-100 mb-4 shadow-sm"
                      >
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2 border-bottom">
                          <h5 className="m-0">{sub.subDimensionName}</h5>
                          <div className="d-flex align-items-center">
                            {/* Zoom */}
                            <button
                              className="border-0 bg-transparent me-2"
                              data-bs-toggle="modal"
                              data-bs-target={`#${modalId}`}
                              onClick={() =>
                                openZoomModal(
                                  modalId,
                                  sub.subDimensionName,
                                  options,
                                  series
                                )
                              }
                            >
                              <img src={ZOOM_ICON} alt="menu" />
                            </button>

                            {/* Dropdown */}
                            <div className="dropdown">
                              <button
                                className=" bg-transparent icon-btn"
                                type="button"
                                data-bs-toggle="dropdown"
                              >
                                <img src={MENU_ICON} alt="menu" />
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      exportAsExcel(responseBody, sub.subDimensionName)
                                    }
                                  >
                                    Download Excel
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      exportAsPDF(chartId, sub.subDimensionName)
                                    }
                                  >
                                    Export PDF
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      exportAsImage(chartId, sub.subDimensionName)
                                    }
                                  >
                                    Download Image
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Chart */}
                        {loading ? (
                          <div className="loader-container"><div className="loader"></div></div>
                        ) : (
                          <div id={chartId} className="px-3 pb-3">
                            <Chart
                              options={options}
                              series={series}
                              type="bar"
                              height={250}
                            />
                          </div>
                        )}
                        {/* Zoom Modal */}
                        <ZoomModal
                          id={modalId}
                          title={zoomData.title}
                          chartOptions={zoomData.chartOptions}
                          seriesData={zoomData.seriesData}
                          type="bar"
                          height={400}
                          remarkText={remarkText}
                          setRemarkText={setRemarkText}
                          showRemarkModal={showRemarkModal}
                          setShowRemarkModal={setShowRemarkModal}
                          modalRef={modalRef}
                          exportSummaryToExcel={exportAsExcel}
                          exportAsPDF={exportAsPDF}
                          exportAsImage={exportAsImage}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Single remark icon for the entire API response */}
              <div className="d-flex justify-content-end ">
                <button
                  title="Add Comment"
                  onClick={() => {
                    setShowRemarkModal(true);
                    setTimeout(() => {
                      if (modalRef.current) modalRef.current.click();
                    }, 0);
                  }}
                  className="border-0 bg-transparent"
                >
                  <img src={CHAT_ICON} alt="chat" />
                </button>
              </div>

              {/* Remark Modal */}
              {showRemarkModal && (
                <Modal modalClick={modalRef} title="Remark" buttonLable="">
                  <Remark
                    selectedCompanyId={selectedCompanyId}
                    selectedCampaignId={selectedCampaignId}
                    type={type}
                    graphName={graphName}
                    initialRemark={remarkText}
                    remarkId={remarkId}
                    isAttempt={0}
                    setRemarkprops={setRemarkText}
                    setRemarkIdProps={setRemarkId}
                    onCloseModal={() => setShowRemarkModal(false)}
                  />
                </Modal>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DimenstionFutureChart;
